import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';

import {environment} from '../../../environments/environment';
import {JWTUser} from '@models/jwt-data.model';
import {Role} from '@models/roles.enum';
import {UserService} from "@services/user.service";
import {PagedResponse} from "@models/response.model";
import {LEGACY_AUTH_REQUIRED_KEY} from "@models/consts/storage-constants.model";
import * as _ from "lodash";
import {map, finalize} from "rxjs/operators";
import {BehaviorSubject, Subject} from "rxjs";
import {JwtHelperService} from "@auth0/angular-jwt";
import {Log} from "ng2-logger";
import {UserBusinessType} from "@models/enums/user-business-type.enum";

/**
 * Service for handling authentication operations, and for retrieving decrypted information
 * about the current user from the stored JWT.
 */
@Injectable()
export class AuthenticationService {
    static readonly USER_TOKEN_KEY = 'user_token';
    static readonly USER_DATA_TOKEN = 'user_data_untill_refresh_token';
    static readonly TECHNICAL_USER_TOKEN_KEY = 'technical_user_token';
    static readonly TECHNICAL_USER_RETURN_URL_KEY = 'technical_user_return_url';
    static readonly TECHNICAL_USER_LEGACY_AUTH_KEY = 'technical_user_legacy_auth';
    static readonly API_LOGIN_MSAL = `${environment.baseURL}/api/authenticate/oauth-bosch-token`;
    static readonly API_LOGIN_BASE_PATH = `${environment.baseURL}/api/authenticate/credentials`;
    static readonly API_LOGIN_BOSCH_ID_BASE_PATH = `${environment.baseURL}/api/authenticate/boschidentifier`;
    static readonly API_FORGOT_PASSWORD = `${environment.baseURL}/api/authenticate/resetPassword`;
    static readonly API_RESET_PASSWORD = `${environment.baseURL}/api/authenticate/resetDefaultPassword`;
    static readonly API_REGISTER = `${environment.baseURL}/api/authenticate/register`;
    static readonly API_UPDATE_BOSCH_ID = `${environment.baseURL}/api/authenticate/resetBoschIdentifier`;
    static readonly API_LOGOUT = `${environment.baseURL}/api/authenticate/logout`;
    static readonly API_GET_ASSIGNED_ROLES = `${environment.baseURL}/api/authenticate/roles`;
    static readonly API_SWITCH_ROLE = `${environment.baseURL}/api/authenticate/switch-role`;
    static readonly API_GET_MISSED_NOTIFICATIONS = `${environment.baseURL}/api/requests/countMissed`;

    _logger = Log.create(AuthenticationService.name);

    // Loading state for MSAL token exchange
    private _msalExchangeLoading$ = new BehaviorSubject<boolean>(false);
    public msalExchangeLoading$ = this._msalExchangeLoading$.asObservable();
    private _userChanged$ = new Subject<void>();
    public userChanged$ = this._userChanged$.asObservable();

    constructor(private http: HttpClient, private jwtHelper: JwtHelperService) {
    }

    /**
     * Check if the user is using legacy authentication (no SSO access)
     */
    isLegacyAuth(): boolean {
        return sessionStorage.getItem(LEGACY_AUTH_REQUIRED_KEY) === 'true';
    }

    /**
     * Set the legacy authentication flag
     */
    setLegacyAuthRequired(required: boolean): void {
        if (required) {
            sessionStorage.setItem(LEGACY_AUTH_REQUIRED_KEY, 'true');
        } else {
            sessionStorage.removeItem(LEGACY_AUTH_REQUIRED_KEY);
        }
    }

    /**
     * Get the MSAL scopes from environment config
     */
    getMsalScopes(): string[] {
        return environment.msalModuleConfig.scopes ? [environment.msalModuleConfig.scopes] : [];
    }

    /**
     * Get the MSAL redirect URI from environment config
     */
    getMsalRedirectUri(): string {
        return environment.msalModuleConfig.redirectUri || window.location.origin;
    }

    /**
     * Clear all authentication storage (both app and prepare for logout)
     */
    clearAuthStorage(): void {
        localStorage.clear();
        sessionStorage.clear();
    }

    preserveTechnicalUserSession(returnUrl: string): void {
        const token = localStorage.getItem(AuthenticationService.USER_TOKEN_KEY);
        if (!token) {
            return;
        }

        sessionStorage.setItem(AuthenticationService.TECHNICAL_USER_TOKEN_KEY, token);
        sessionStorage.setItem(AuthenticationService.TECHNICAL_USER_RETURN_URL_KEY, returnUrl || '/dashboard');
        sessionStorage.setItem(
            AuthenticationService.TECHNICAL_USER_LEGACY_AUTH_KEY,
            this.isLegacyAuth() ? 'true' : 'false'
        );
    }

    hasPreservedTechnicalUserSession(): boolean {
        return !!sessionStorage.getItem(AuthenticationService.TECHNICAL_USER_TOKEN_KEY);
    }

    clearPreservedTechnicalUserSession(): void {
        sessionStorage.removeItem(AuthenticationService.TECHNICAL_USER_TOKEN_KEY);
        sessionStorage.removeItem(AuthenticationService.TECHNICAL_USER_RETURN_URL_KEY);
        sessionStorage.removeItem(AuthenticationService.TECHNICAL_USER_LEGACY_AUTH_KEY);
    }

    restoreTechnicalUserSession(): string | null {
        const technicalToken = sessionStorage.getItem(AuthenticationService.TECHNICAL_USER_TOKEN_KEY);
        const returnUrl = sessionStorage.getItem(AuthenticationService.TECHNICAL_USER_RETURN_URL_KEY);
        const legacyAuthRequired = sessionStorage.getItem(AuthenticationService.TECHNICAL_USER_LEGACY_AUTH_KEY) === 'true';

        if (!technicalToken) {
            return null;
        }

        this.clearAuthStorage();
        this.storeToken(technicalToken);
        this.setLegacyAuthRequired(legacyAuthRequired);
        this._userChanged$.next();

        return returnUrl;
    }

    exchangeMsalTokenForJwt(accessToken: string) {
        this._msalExchangeLoading$.next(true);
        return this.http.post(`${AuthenticationService.API_LOGIN_MSAL}`, {accessToken}).pipe(
            map(response => {
                this.handleLoginSuccess(response);
                return response;
            }),
            finalize(() => this._msalExchangeLoading$.next(false))
        );
    }

    login(user) {
        return this.http.post(`${AuthenticationService.API_LOGIN_BASE_PATH}`, user).pipe(
            map(response => {
                const userDataToken = btoa(JSON.stringify({identity: user.identity, password: user.password}));
                localStorage.setItem(AuthenticationService.USER_DATA_TOKEN, userDataToken);
                this.handleLoginSuccess(response);
                return response;
            })
        );
    }

    loginWithBoschId(boschId: string, selectedItem?: { id: number; type: 'request' | 'return' }) {
        const body: any = {boschId};

        if (selectedItem) {
            body.selectedItem = selectedItem;
        }

        return this.http.post(`${AuthenticationService.API_LOGIN_BOSCH_ID_BASE_PATH}`, body).pipe(
            map(response => {
                this.handleLoginSuccess(response);
                return response;
            })
        );
    }

    register(user) {
        user = _(user).omitBy(_.isNil).omitBy(_.isNaN).omitBy(_.isEmpty).value();
        return this.http.post<PagedResponse<any>>(`${AuthenticationService.API_REGISTER}`, user);
    }

    addUserBoschId(boschId) {
        return this.http.patch(`${UserService.API_USERS_BASE_PATH}`, {"boschId": boschId}).pipe(
            map(response => {
                this.handleLoginSuccess(response);
                return response;
            })
        );
    }

    forgotPassword(data) {
        const httpOptions = {
            headers: new HttpHeaders({
                'BoschId': data.boschId,
            })
        };
        return this.http.post(`${AuthenticationService.API_FORGOT_PASSWORD}`, data.formValues, httpOptions)
    }

    resetPassword(user) {
        return this.http.post(`${AuthenticationService.API_RESET_PASSWORD}`, user);
    }

    updateBoschID(data) {
        return this.http.post(`${AuthenticationService.API_UPDATE_BOSCH_ID}`, data);
    }

    logout() {
        return this.http.post(`${AuthenticationService.API_LOGOUT}`, null);
    }

    storeToken(token: string): void {
        try {
            this._logger.info("Store token in local storage");
            localStorage.setItem(AuthenticationService.USER_TOKEN_KEY, token);
        } catch (error) {
            this._logger.error("Failed to store token in local storage", error);
        }
    }

    getUserDetails(): JWTUser {
        const token = localStorage.getItem(AuthenticationService.USER_TOKEN_KEY);
        if (!token) {
            return null;
        }

        const decodedToken = this.jwtHelper.decodeToken(token);
        return new JWTUser(decodedToken);
    }

    isCurrentUserWithRole(role: Role) {
        const userDetails = this.getUserDetails();
        return userDetails ? userDetails.role === role : true;
    }

    isAllowedLogin() {
        const userDetails = this.getUserDetails();
        if (userDetails == null) {
            return false;
        }

        if (userDetails.userBusinessType === UserBusinessType.TechnicalProductionUser ||
            userDetails.userBusinessType === UserBusinessType.TechnicalToolManagementUser) {
            return true;
        }

        return userDetails.role != null;
    }

    hasPermission(requiredPermission: string): boolean {
        const userDetails = this.getUserDetails();
        return !!(userDetails?.permissions && userDetails.permissions.indexOf(requiredPermission) > -1);
    }

    getAssignedRoles() {
        return this.http.get<any>(`${AuthenticationService.API_GET_ASSIGNED_ROLES}`).pipe(
            map((response: any) => {
                if (Array.isArray(response)) {
                    return response;
                }

                if (response && Array.isArray(response.roles)) {
                    return response.roles;
                }

                return [];
            })
        );
    }

    switchRole(roleId: number) {
        return this.http.post(`${AuthenticationService.API_SWITCH_ROLE}`, {roleId}).pipe(
            map((response: any) => {
                this.handleLoginSuccess(response);
                return this.getUserDetails();
            })
        );
    }

    getMissedNotifications(plantIds?: string[]) {
        let params = new HttpParams();
        if (plantIds) {
            plantIds.forEach(plant => {
                params = params.append('PlantIds', plant)
            });
        }

        return this.http.get(`${AuthenticationService.API_GET_MISSED_NOTIFICATIONS}`, {params});
    }

    private handleLoginSuccess(data) {
        this._logger.info("Handle login success.");
        this.storeToken(data.token);
        this._userChanged$.next();
    }
}
